'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useUserRoles } from '@/hooks/useUserRoles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { toast } from 'sonner'
import { LogOut, User, History, Settings, Shield, Zap } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

export function UserMenu() {
  const { user, isLoading: isAuthLoading, signOut } = useAuth()
  const { roles, isLoading: rolesLoading, error: rolesError } = useUserRoles(user?.id)
  const [menuItems, setMenuItems] = useState<React.ReactNode[]>([])
  const router = useRouter()
  const t = useTranslations('user')

  const getMenuItemsByRole = useCallback(() => {
    const baseItems = [
      <DropdownMenuItem key="profile" asChild>
        <Link href="/customer/dashboard" className="w-full flex items-center">
          <User className="mr-2 h-4 w-4" />
          <span>{t('profile')}</span>
        </Link>
      </DropdownMenuItem>,
      <DropdownMenuItem key="bookings" asChild>
        <Link href="/customer/bookings" className="w-full flex items-center">
          <History className="mr-2 h-4 w-4" />
          <span>{t('reservations')}</span>
        </Link>
      </DropdownMenuItem>,
    ];

    if (rolesError) {
      console.error("Error loading roles for menu, showing default items.", rolesError);
      return baseItems;
    }

    if (!user || rolesLoading) {
        return baseItems;
    }
    
    const safeRoles = Array.isArray(roles) ? roles : [];
    let dynamicItems: React.ReactNode[] = [];

    if (safeRoles.includes('admin')) {
      dynamicItems.push(
        <DropdownMenuItem key="admin-panel" asChild>
          <Link href="/admin/dashboard" className="w-full flex items-center">
            <Shield className="mr-2 h-4 w-4" />
            <span>{t('adminPanel')}</span>
          </Link>
        </DropdownMenuItem>
      );
    }
    if (safeRoles.includes('company')) {
      dynamicItems.push(
        <DropdownMenuItem key="company-panel" asChild>
          <Link href="/company/dashboard" className="w-full flex items-center">
            <Zap className="mr-2 h-4 w-4" />
            <span>{t('companyPanel')}</span>
          </Link>
        </DropdownMenuItem>
      );
    }

    return [...baseItems, ...dynamicItems];
  }, [user, roles, rolesLoading, rolesError, t]);

  useEffect(() => {
    const items = getMenuItemsByRole();
    setMenuItems(items);
  }, [getMenuItemsByRole]);

  const getInitials = () => {
    if (!user) return 'ZY'

    const firstName = user.user_metadata.first_name || ''
    const lastName = user.user_metadata.last_name || ''

    if (!firstName && !lastName) return 'ZY'

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getFullName = () => {
    if (!user) return ''
    return `${user.user_metadata.first_name || ''} ${user.user_metadata.last_name || ''}`.trim()
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      setMenuItems([])
      router.push('/login')
      toast.success(t('logoutSuccess'))
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error(t('logoutError'))
    }
  }

  if (isAuthLoading || (user && rolesLoading)) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8 border border-amber-600/20">
              <AvatarFallback className="bg-amber-50 text-amber-800">ZY</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">Yükleniyor...</p>
            </div>
          </DropdownMenuLabel>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (!user) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className="h-8 w-8 border border-amber-600/20">
            <AvatarImage
              src={user.user_metadata.avatar_url || ''}
              alt={getFullName()}
            />
            <AvatarFallback className="bg-amber-50 text-amber-800">{getInitials()}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{getFullName()}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {menuItems.length > 0 ? menuItems : (
            <DropdownMenuItem disabled>{t('noActionsAvailable')}</DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('logout')}</span>
          <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 